import type { GoogleAnalyticsEventName, GoogleAnalyticsEventProperties } from './google-analytics.events';

/**
 * Restricts which events a context source contributes params to. Modeled as a discriminated union so that supplying
 * an allow-list and a deny-list at the same time is not expressible: a source either opts in to a specific set of
 * events or opts out of a specific set, never both. Omitting the filter applies the source to every event.
 */
export type GoogleAnalyticsContextSourceFilter =
    | {
          /** Never supplied. Declared only so that `only` and `except` cannot be combined on one filter. */
          except?: never;

          /** The only events this source applies to. Every other event receives nothing from the source. */
          only: readonly GoogleAnalyticsEventName[];
      }
    | {
          /** The events this source is excluded from. Every other event receives the source's params. */
          except: readonly GoogleAnalyticsEventName[];

          /** Never supplied. Declared only so that `only` and `except` cannot be combined on one filter. */
          only?: never;
      };

/**
 * Produces the ambient params a source contributes to a single event. This is evaluated at event-fire time rather
 * than at registration time, so the params always reflect current state; a source must never hand over a snapshot.
 *
 * @param eventName - the event about to be pushed, for sources whose params vary by event
 *
 * @returns the params to merge into the event, or undefined when the source has nothing to contribute right now
 */
export type GoogleAnalyticsContextParamsProvider = (
    eventName: GoogleAnalyticsEventName
) => GoogleAnalyticsEventProperties | undefined;

/**
 * Unregisters the context source it was returned for. Returned from `addContextSource` so a caller (typically a
 * React effect cleanup) can tear the source down without having to remember its name.
 */
export type GoogleAnalyticsContextSourceDisposer = () => void;

/**
 * A named supplier of ambient params that are merged into events as they fire. Context sources exist so that state
 * living outside a call site (an active VIN session, an open Techline case) can enrich analytics events without
 * every call site having to thread that state through by hand.
 */
export type GoogleAnalyticsContextSource = {
    /** Which events the source applies to. Omit to apply the source to every event. */
    filter?: GoogleAnalyticsContextSourceFilter;

    /** Live callback invoked as each event fires, returning the params this source contributes. */
    getParams: GoogleAnalyticsContextParamsProvider;

    /** Registration key. Registering a second source under the same name replaces the first. */
    name: string;
};

/**
 * Decides whether a context source contributes params to the given event, per its filter. Kept as a standalone
 * predicate so the filter semantics are testable on their own and stay out of the service's merge loop.
 *
 * @param source - the registered context source being considered
 * @param eventName - the event about to be pushed
 *
 * @returns true when the source's params should be merged into the event
 *
 * @example
 * doesContextSourceApplyToEvent({ name: 'vin', getParams, filter: { only: ['search'] } }, 'search'); // true
 * doesContextSourceApplyToEvent({ name: 'vin', getParams, filter: { only: ['search'] } }, 'logout'); // false
 */
export function doesContextSourceApplyToEvent(
    source: GoogleAnalyticsContextSource,
    eventName: GoogleAnalyticsEventName
): boolean {
    const { filter } = source;

    // No filter means the source is ambient for every event.
    if (!filter) {
        return true;
    }

    // Allow-list form: only the listed events are enriched.
    if (filter.only !== undefined) {
        return filter.only.includes(eventName);
    }

    // Deny-list form: every event except the listed ones is enriched.
    if (filter.except !== undefined) {
        return !filter.except.includes(eventName);
    }

    return true;
}
